/**
 * useAccountSupervision — polls /api/v1/accounts/supervision/status.
 *
 * Read-mostly. The `runNow` callback POSTs /run with allow_auto_correct=false
 * (paper-only diagnostic). The hook NEVER sends broker credentials, NEVER
 * accepts overrides for live trading, and treats any non-2xx as a transient
 * error that should not clobber the last good render.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiV1Base } from '../lib/apiConfig';

export type NoTradeStatus = 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'EMERGENCY' | 'UNKNOWN';
export type RiskStatus = 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN';
export type ReviewStatus = 'CURRENT' | 'REVIEW_OVERDUE' | 'NEVER_REVIEWED';

export interface SupervisionAccount {
  account_label: string;
  account_id_masked: string;
  mode: string;
  broker: string | null;
  equity: number | null;
  cash: number | null;
  buying_power: number | null;
  open_positions_count: number;
  daily_pnl: number | null;
  daily_pnl_percent: number | null;
  total_pnl: number | null;
  last_trade_at: string | null;
  last_order_at: string | null;
  last_fill_at: string | null;
  last_review_at: string | null;
  idle_hours: number | null;
  idle_days: number | null;
  no_trade_status: NoTradeStatus;
  risk_status: RiskStatus;
  review_status: ReviewStatus;
  strategy_assigned: string | null;
  worker_assigned: string | null;
  worker_status: string | null;
  action_required: string[];
  actions_taken: string[];
  actions_blocked: string[];
  blockers: string[];
  next_review_at: string | null;
  positions: Array<{
    symbol: string;
    qty: number | null;
    avg_entry_price: number | null;
    current_price: number | null;
    unrealized_pnl: number | null;
    unrealized_pnl_percent: number | null;
    classification: string;
  }>;
  alert_codes: string[];
}

export interface SupervisionStatus {
  run_id: string;
  started_at: string;
  finished_at: string;
  status: string;
  auto_correct_enabled: boolean;
  report_path: string | null;
  errors: string[];
  accounts: SupervisionAccount[];
  counts: {
    total: number;
    by_no_trade_status: Record<string, number>;
    by_review_status: Record<string, number>;
    by_risk_status: Record<string, number>;
  };
  forex_no_trade_summary: Record<string, unknown> | null;
}

export interface UseAccountSupervisionResult {
  status: SupervisionStatus | null;
  liveTradingBlocked: boolean;
  loading: boolean;
  error: string | null;
  reportPath: string | null;
  refetch: () => void;
  runNow: () => Promise<void>;
  running: boolean;
}

const POLL_INTERVAL_MS = 60_000;

export function useAccountSupervision(): UseAccountSupervisionResult {
  const [status, setStatus] = useState<SupervisionStatus | null>(null);
  const [liveTradingBlocked, setLiveTradingBlocked] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const firstLoadRef = useRef<boolean>(true);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${apiV1Base()}/accounts/supervision/status`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const body = await resp.json();
      const data = body?.data ?? body;
      const latest = data?.latest_run ?? null;
      setLiveTradingBlocked(Boolean(data?.live_trading_blocked ?? true));
      if (latest) setStatus(latest as SupervisionStatus);
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load account supervision';
      if (firstLoadRef.current) {
        setError(msg);
        setLoading(false);
        firstLoadRef.current = false;
      }
      // After first load, swallow transient errors so we don't clobber UI.
    }
  }, []);

  const runNow = useCallback(async () => {
    setRunning(true);
    try {
      const resp = await fetch(`${apiV1Base()}/accounts/supervision/run?allow_auto_correct=false`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to run account supervision';
      setError(msg);
    } finally {
      setRunning(false);
    }
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (!cancelled) await load();
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [load]);

  return {
    status,
    liveTradingBlocked,
    loading,
    error,
    reportPath: status?.report_path ?? null,
    refetch: load,
    runNow,
    running,
  };
}
